/**
 * /github-callback — SPA-driven OAuth completion + repo picker.
 *
 * Flow:
 *   1. GitHub redirects here with `?code=…&state=appId:userId:returnToB64`
 *   2. POST /api/github/exchange to store the encrypted token + capture appId
 *   3. Open RepoPickerModal so the user picks which repo this app mirrors to
 *   4. POST /api/apps/:appId/github/connect-repo
 *   5. Navigate to the editor for that app
 *
 * If the user closes the picker without choosing, we drop them on /apps
 * so they're never stuck on this blank page.
 */

import React, { useEffect, useState } from 'react';

import RepoPickerModal from '@/features/auth/components/repo-picker-modal/RepoPickerModal';
import {
  githubApi,
  type UserRepoListItem,
} from '@/features/editor/workspaces/versions/api/github.api';

type Phase =
  | { kind: 'exchanging' }
  | { kind: 'picking'; appId: string; githubLogin: string }
  | { kind: 'connecting'; appId: string; owner: string; repo: string }
  | { kind: 'error'; message: string };

export default function GithubCallback() {
  const [phase, setPhase] = useState<Phase>({ kind: 'exchanging' });
  const [repos, setRepos] = useState<UserRepoListItem[]>([]);
  const [reposLoading, setReposLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state') || '';
    const errorParam = params.get('error');
    const errorDesc = params.get('error_description');

    if (errorParam) {
      setPhase({ kind: 'error', message: errorDesc || errorParam });
      return;
    }
    if (!code) {
      setPhase({ kind: 'error', message: 'Missing OAuth code in callback URL.' });
      return;
    }

    (async () => {
      try {
        const res = await githubApi.exchange(code, state);

        // Strip the OAuth params so a page refresh doesn't try to re-exchange.
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        window.history.replaceState(null, '', url.toString());

        if (res.alreadyConnected) {
          // Reconnect of an already-linked app — straight to the editor.
          window.location.assign(`/?appId=${encodeURIComponent(res.appId)}`);
          return;
        }

        setPhase({ kind: 'picking', appId: res.appId, githubLogin: res.githubLogin });
        setReposLoading(true);
        try {
          const r = await githubApi.userRepos(res.appId);
          setRepos(r.repos);
        } finally {
          setReposLoading(false);
        }
      } catch (e: any) {
        setPhase({ kind: 'error', message: e?.message || 'Exchange failed.' });
      }
    })();
  }, []);

  const handlePick = async (repo: UserRepoListItem) => {
    if (phase.kind !== 'picking') return;
    const { appId } = phase;
    setPhase({ kind: 'connecting', appId, owner: repo.owner, repo: repo.repo });
    try {
      await githubApi.connectRepo(appId, repo.owner, repo.repo);
      window.location.assign(`/?appId=${encodeURIComponent(appId)}&github=connected`);
    } catch (e: any) {
      setPhase({ kind: 'error', message: e?.message || 'Repo connect failed.' });
    }
  };

  const handleSkip = () => {
    // User dismissed the picker without choosing. Drop them back on /apps
    // — the OAuth token is stored, they can pick a repo later from the card menu.
    window.location.assign('/apps?github=pending');
  };

  if (phase.kind === 'exchanging') {
    return <CenteredMessage title="Finishing GitHub sign-in…" sub="One sec." />;
  }
  if (phase.kind === 'error') {
    return (
      <CenteredMessage
        title="GitHub connect failed"
        sub={phase.message}
        action={{ label: 'Back to apps', href: '/apps' }}
      />
    );
  }
  if (phase.kind === 'connecting') {
    return <CenteredMessage title="Connecting repository…" sub={`${phase.owner}/${phase.repo}`} />;
  }
  // phase.kind === 'picking'
  return (
    <>
      <CenteredMessage
        title={`Hi ${phase.githubLogin} — pick a repository`}
        sub="Choose which GitHub repo this app should mirror to."
      />
      <RepoPickerModal
        appId={phase.appId}
        repos={repos}
        loading={reposLoading}
        onPick={handlePick}
        onCancel={handleSkip}
      />
    </>
  );
}

export function CenteredMessage({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: string;
  action?: { label: string; href: string; onClick?: (e: React.MouseEvent) => void };
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 8,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        background: '#fafafa',
        color: '#111',
        padding: 24,
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 600 }}>{title}</div>
      {sub && (
        <div style={{ fontSize: 13, color: '#555', textAlign: 'center', maxWidth: 480 }}>{sub}</div>
      )}
      {action && (
        <a
          href={action.href}
          onClick={action.onClick}
          style={{
            marginTop: 12,
            padding: '6px 14px',
            border: '1px solid #ddd',
            borderRadius: 6,
            color: '#111',
            background: '#fff',
            textDecoration: 'none',
            fontSize: 13,
          }}
        >
          {action.label}
        </a>
      )}
    </div>
  );
}
